/* Copyright (c) 2023 MOCA Systems, Inc.  All rights reserved. */

import { AnalyticsStateService } from 'src/app/services/analytics-state.service';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { RouteUtils } from 'src/app/services/route-utils.service';
import { logger } from '@touchplan/logging';
import { fullFirebasePath, getAnalyticsDecoratedUserId } from './getAnalyticsDecoratedUserId';
import { IAnalyticsUser } from './AnalyticsUser.interface';
import { skip } from 'rxjs/operators';

declare const window: any;
declare const Intercom: any;

/**
 * Statement of purpose
 * - thin (typed) wrapper around analytics.js
 * - bridges angular and "non-angular" worlds
 */
export class AnalyticsService {
	public featureFlagManager: FeatureFlagManager;
	private routeUtils: RouteUtils;
	private analyticsStateService: AnalyticsStateService;

	//basically just moved here for easier unit testing...
	private get analytics() {
		return window.analytics;
	}

	private startListeners() {
		this.analyticsStateService.listenForIdentification().subscribe((userInfo) => {
			this.identify(getAnalyticsDecoratedUserId(userInfo.id), userInfo.userObject);
		});

		//fires a page as part of initialization, so skip the first one
		this.routeUtils
			.routeNameChange$()
			.pipe(skip(1))
			.subscribe((s) => {
				this.analytics.page();
			});
	}

	public start(
		analyticsState: AnalyticsStateService,
		routeUtils: RouteUtils,
		featureFlagManager: FeatureFlagManager
	) {
		this.analyticsStateService = analyticsState;
		this.routeUtils = routeUtils;
		this.featureFlagManager = featureFlagManager;

		this.startListeners();
	}

	public getGlobals(arr: string[]) {
		let obj: any = {};
		obj = this.analyticsStateService.getProjectProps(arr);

		const idx = arr.indexOf('state');
		if (idx !== -1) {
			obj['state'] = this.routeUtils.routeName();
		}

		return obj;
	}

	public track(event: string, properties: object = {}, options?: object, callback?: () => void) {
		const decoratedProps = Object.assign({ firebaseInstance: fullFirebasePath, ...properties });
		this.analytics.track(event, decoratedProps, options, () => {
			// BEGIN PLACEHOLDER REPLACE AS PART OF TP-1548
			// - decide what to do with this
			// calling this updates the person data in intercom and checks for messages
			// and is throtted for 30mins if we hit this more than 20 times in 30mins, so how we're using it is... pretty incorrect
			// https://developers.intercom.com/installing-intercom/docs/intercom-javascript#intercomupdate
			// leaving it as is for now
			if (Intercom) {
				Intercom('update');
			}
			// END PLACEHOLDER REPLACE AS PART OF TP-1548
			if (callback) {
				callback();
			}
		});
	}
	// consider deletion and replacing with a "blocking" pre-logout hook on authService
	public reset() {
		this.analytics.reset();

		//analytics reset doesn't call this apparently
		if (window.Intercom) {
			Intercom('shutdown');
		}
	}

	/**
	 * "logs in" a user to the analytics sytems, and updates the record with the latest properties
	 * @param analyticsUserId expects the analytics decorated id
	 * @param userProperties an object of properties to update
	 * @param callback waits 300ms...but inside the library
	 */
	public identify(
		analyticsUserId: string | undefined,
		userProperties?: IAnalyticsUser,
		callback?: () => void
	) {
		//one of those "javascript-y" api's where you can provide the arguements in any order and any number of them...
		//we're... not going to do that and support only the options we use in a wrapper

		if (analyticsUserId) {
			this.analytics.identify(analyticsUserId, userProperties, undefined, callback);
		} else {
			this.analytics.identify(userProperties, callback);
		}
	}

	// START PLACEHOLDER REPLACE AS PART OF TP-1548
	// - only currently used for knowing when intercom is loaded (replace the stuff using this too)
	// - might be the only reason segment is blocking intercom (via adblockers)
	public ready(cb: () => void) {
		this.analytics.ready(cb);
	}
	// END PLACEHOLDER REPLACE AS PART OF TP-1548
}
export const analyticsService = new AnalyticsService();

/** convert an array into analytics safe */
export function convertArrayToString(arr: Array<string | number>) {
	if (!arr || !arr.length) {
		return null;
	}
	return arr.join(', ');
}
